import { getExchangeRates } from "@redotech/redo-common-model/money/get-exchange-rates";
import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { getAddressCollectionsForTeam } from "./schema/addresses/get-address-collections-for-team";
import { bulkCreateAeoProspects } from "./schema/aeo/bulk-create-aeo-prospects";
import { cancelAeoOnboarding } from "./schema/aeo/cancel-aeo-onboarding";
import { checkBrightdataRunStatus } from "./schema/aeo/check-brightdata-run-status";
import { exportAeoMarkdown } from "./schema/aeo/export-aeo-markdown";
import { exportAeoPromptResults } from "./schema/aeo/export-aeo-prompt-results";
import { generateMerchantBrandIdentityGuide } from "./schema/aeo/generate-merchant-brand-identity-guide";
import { getAeoDashboardAnalytics } from "./schema/aeo/get-aeo-dashboard-analytics";
import { getAeoOnboardingState } from "./schema/aeo/get-aeo-onboarding-state";
import { getAgentPromptTesterConfig } from "./schema/aeo/get-agent-prompt-tester-config";
import { getBatchAeoJobStatus } from "./schema/aeo/get-batch-aeo-job-status";
import { getAdminBrandIdentityGuide } from "./schema/aeo/get-brand-identity-guide";
import { previewAgentPromptTemplate } from "./schema/aeo/preview-agent-prompt-template";
import { reindexAeoKnowledge } from "./schema/aeo/reindex-aeo-knowledge";
import { runAgentPromptTest } from "./schema/aeo/run-agent-prompt-test";
import { setAdminBrandAliases } from "./schema/aeo/set-admin-brand-aliases";
import { startAeoOnboarding } from "./schema/aeo/start-aeo-onboarding";
import { startBatchAeoOnboarding } from "./schema/aeo/start-batch-aeo-onboarding";
import { startBatchBrandAndCompetitorMention } from "./schema/aeo/start-batch-brand-and-competitor-mention";
import { startBatchBrandCompetitorDiscovery } from "./schema/aeo/start-batch-brand-competitor-discovery";
import { startBatchCompetitorCandidateValidation } from "./schema/aeo/start-batch-competitor-candidate-validation";
import { startBatchGenerateCompetitorAliases } from "./schema/aeo/start-batch-generate-competitor-aliases";
import { updateBrandIdentityGuideIdentity } from "./schema/aeo/update-brand-identity-guide-identity";
import { generateSettingsAgentResponse } from "./schema/agents/generate-settings-agent-response";
import { getMostRecentAIContent } from "./schema/ai-content/get-ai-content";
import { getAiSettingsRequestStats } from "./schema/ai-settings-requests/get-ai-settings-request-stats";
import { checkAmpRegistrationStatus } from "./schema/amp/check-amp-registration-status";
import { getAmpRegistrationFormAnswers } from "./schema/amp/get-amp-registration-form-answers";
import { getDefaultSenderAddressForTeam } from "./schema/amp/get-default-sender-address-for-team";
import { getDkimVerifiedFromAddressesForTeam } from "./schema/amp/get-dkim-verified-from-addresses-for-team";
import { getEmailDomainsForTeam } from "./schema/amp/get-email-domains-for-team";
import { sendDemoAmpEmailsForTeam } from "./schema/amp/send-demo-amp-emails-for-team";
import { generateReturnInsights } from "./schema/analytics/generate-return-insights";
import { getGlobalAiExchangeAgentAnalytics } from "./schema/analytics/get-global-ai-exchange-agent-analytics";
import { getWarehouseSlasDashboard } from "./schema/analytics/get-warehouse-slas-dashboard";
import {
  getAskRedoEmergencyMode,
  updateAskRedoEmergencyMode,
} from "./schema/ask-redo/ask-redo-emergency-mode";
import { exportAskRedoExplorerThreadsCsv } from "./schema/ask-redo/export-explorer-threads-csv";
import { getAskRedoChildThreads } from "./schema/ask-redo/get-child-threads";
import { getAskRedoExplorerAggregates } from "./schema/ask-redo/get-explorer-aggregates";
import { getAskRedoExplorerThreads } from "./schema/ask-redo/get-explorer-threads";
import { getIntercomDailyMetrics } from "./schema/ask-redo/get-intercom-daily-metrics";
import { getAskRedoThreads } from "./schema/ask-redo/get-merchant-support-threads";
import { getAskRedoToolMetrics } from "./schema/ask-redo/get-merchant-support-tool-metrics";
import { getAskRedoThreadById } from "./schema/ask-redo/get-thread-by-id";
import { getAuditLog, getAuditLogs } from "./schema/audit-logs/audit-logs";
import { getCurrentUserRole } from "./schema/auth/get-current-user-role";
import { addBalanceCredit } from "./schema/balance/add-balance-credit";
import { getBalance } from "./schema/balance/get-balance";
import { getBalanceScheduleInfo } from "./schema/balance/get-balance-schedule-info";
import { upsertBalanceSchedule } from "./schema/balance/upsert-balance-schedule";
import { createPayouts } from "./schema/billing/create-payouts";
import { createBrand } from "./schema/brands/create-brand";
import { deleteBrand } from "./schema/brands/delete-brand";
import { getBrands } from "./schema/brands/get-brands";
import { updateBrand } from "./schema/brands/update-brand";
import { createCarrierAccount } from "./schema/carrier-accounts/create-carrier-account";
import { createTestLabel } from "./schema/carrier-accounts/create-test-label";
import { deleteCarrierAccount } from "./schema/carrier-accounts/delete-carrier-account";
import { getCarrierAccounts } from "./schema/carrier-accounts/get-carrier-accounts";
import { addRedoCarrierService } from "./schema/conversion/add-redo-carrier-service";
import { bulkUpdateDeliveryProfileProducts } from "./schema/conversion/bulk-update-delivery-profile-products";
import { canAutoOnboardCheckoutOptimization } from "./schema/conversion/can-auto-onboard-checkout-optimization";
import { canStartHalfRedoCheckoutAbTest } from "./schema/conversion/can-start-half-redo-checkout-ab-test";
import { canStartRescuePanelSplit } from "./schema/conversion/can-start-rescue-panel-split";
import { canUseShippingOptionMetafields } from "./schema/conversion/can-use-shipping-option-metafields";
import { concludeRescuePanelSplit } from "./schema/conversion/conclude-rescue-panel-split";
import { executeCheckoutSplitAgent } from "./schema/conversion/execute-checkout-split-agent";
import {
  adoptGaleMerchant,
  getGaleOnboardingStatus,
  prepareGaleKycLink,
  registerGaleMerchant,
  reissueGaleApiKey,
  updateGalePricing,
} from "./schema/conversion/gale-onboarding";
import { getAutoOnboardCheckoutOptimizationStatus } from "./schema/conversion/get-auto-onboard-checkout-optimization-status";
import { getCalculatedFulfillmentDelay } from "./schema/conversion/get-calculated-fulfillment-delay";
import { getCheckoutExperiences } from "./schema/conversion/get-checkout-experiences";
import { getConversionHealthCheck } from "./schema/conversion/get-conversion-health-check";
import { getConversionSettings } from "./schema/conversion/get-conversion-settings";
import { getConversionTeamDetail } from "./schema/conversion/get-conversion-team-detail";
import { getDeliveryProfileProductCount } from "./schema/conversion/get-delivery-profile-product-count";
import { getHsaPaymentAnalytics } from "./schema/conversion/get-hsa-payment-analytics";
import { getHsaPaymentAnalyticsForTeam } from "./schema/conversion/get-hsa-payment-analytics-for-team";
import { getHsaSettings } from "./schema/conversion/get-hsa-settings";
import { getIntentSelfServeTrials } from "./schema/conversion/get-intent-self-serve-trials";
import { getIntentStatus } from "./schema/conversion/get-intent-status";
import { getLongTermConversionMonitor } from "./schema/conversion/get-long-term-conversion-monitor";
import { getPpuIntentSelfServeMerchants } from "./schema/conversion/get-ppu-intent-self-serve-merchants";
import { getRescuePanelSplitsWithMetrics } from "./schema/conversion/get-rescue-panel-splits-with-metrics";
import { getSyncDeliveryProfileProductMetafieldsNowStatus } from "./schema/conversion/get-sync-delivery-profile-product-metafields-now-status";
import { getTeamCheckoutProbabilitySpotlights } from "./schema/conversion/get-team-checkout-probability-spotlights";
import { hideInvisibleRateNameCharsAllowed } from "./schema/conversion/hide-invisible-rate-name-chars-allowed";
import { listDeliveryProfileNames } from "./schema/conversion/list-delivery-profile-names";
import {
  getSetupLandedCostDemoCheckoutTreeStatus,
  setupLandedCostDemoCheckoutTree,
} from "./schema/conversion/setup-landed-cost-demo-checkout-tree";
import { splitLocationGroups } from "./schema/conversion/split-location-groups";
import { startHalfRedoCheckoutAbTest } from "./schema/conversion/start-half-redo-checkout-ab-test";
import { startRescuePanelSplit } from "./schema/conversion/start-rescue-panel-split";
import { subscriptionsModeAllowed } from "./schema/conversion/subscriptions-mode-allowed";
import { syncDeliveryProfileProductMetafieldsNow } from "./schema/conversion/sync-delivery-profile-product-metafields-now";
import { triggerAutoOnboardCheckoutOptimization } from "./schema/conversion/trigger-auto-onboard-checkout-optimization";
import { updateConversionBillingSettings } from "./schema/conversion/update-conversion-billing-settings";
import { updateConversionSettings } from "./schema/conversion/update-conversion-settings";
import { updateHsaSettings } from "./schema/conversion/update-hsa-settings";
import { createCoverageSplitGeneration } from "./schema/coverage-splits/create-coverage-split-generation";
import { getCoverageSplitLineage } from "./schema/coverage-splits/get-coverage-split-lineage";
import { setCtxPlan } from "./schema/ctx/set-ctx-plan";
import { getCustomAnalyticsCatalogPreview } from "./schema/custom-reports/get-custom-analytics-catalog-preview";
import { getCustomReportDebug } from "./schema/custom-reports/get-custom-report-debug";
import { getCustomReportsUsage } from "./schema/custom-reports/get-custom-reports-usage";
import { listCustomReports } from "./schema/custom-reports/list-custom-reports";
import { lookupCustomReportId } from "./schema/custom-reports/lookup-custom-report-id";
import { getMultipassSecret } from "./schema/customer-accounts/get-multipass-secret";
import { setMultipassSecret } from "./schema/customer-accounts/set-multipass-secret";
import { createCustomerEventsBackfill } from "./schema/customer-events-backfill/create-customer-events-backfill";
import { getCustomerEventsBackfill } from "./schema/customer-events-backfill/get-customer-events-backfill";
import { listCustomerImports } from "./schema/customer-imports/list-customer-imports";
import { startCustomerImport } from "./schema/customer-imports/start-customer-import";
import { getReclaimAccessDefaults } from "./schema/disputes/get-reclaim-access-defaults";
import { getReclaimAlerts } from "./schema/disputes/get-reclaim-alerts";
import { getReclaimAlertsCount } from "./schema/disputes/get-reclaim-alerts-count";
import { getReclaimDealSizing } from "./schema/disputes/get-reclaim-deal-sizing";
import { getReclaimDeflections } from "./schema/disputes/get-reclaim-deflections";
import { getReclaimDeflectionsCount } from "./schema/disputes/get-reclaim-deflections-count";
import { getReclaimDisputes } from "./schema/disputes/get-reclaim-disputes";
import { getReclaimDisputesCount } from "./schema/disputes/get-reclaim-disputes-count";
import { getReclaimGenerationOverdueDisputes } from "./schema/disputes/get-reclaim-generation-overdue-disputes";
import { getReclaimInFlight } from "./schema/disputes/get-reclaim-in-flight";
import { getReclaimMatureCohorts } from "./schema/disputes/get-reclaim-mature-cohorts";
import {
  getReclaimMerchantOpportunities,
  getReclaimMerchantOpportunityRows,
  getReclaimMerchantOpportunitySummary,
} from "./schema/disputes/get-reclaim-merchant-opportunities";
import { getReclaimMerchants } from "./schema/disputes/get-reclaim-merchants";
import { getReclaimPipelineHealth } from "./schema/disputes/get-reclaim-pipeline-health";
import { getReclaimWinRate } from "./schema/disputes/get-reclaim-win-rate";
import { getReclaimWinRateByBank } from "./schema/disputes/get-reclaim-win-rate-by-bank";
import { getReclaimWinRateByReason } from "./schema/disputes/get-reclaim-win-rate-by-reason";
import { getReclaimWinRateByReasonAndBank } from "./schema/disputes/get-reclaim-win-rate-by-reason-and-bank";
import { getReclaimWinRateOverTime } from "./schema/disputes/get-reclaim-win-rate-over-time";
import { regenerateAllDisputeEvidence } from "./schema/disputes/regenerate-all-dispute-evidence";
import { setDisputeReviewed } from "./schema/disputes/set-dispute-reviewed";
import { updateReclaimAccessDefaults } from "./schema/disputes/update-reclaim-access-defaults";
import { searchEmployees } from "./schema/employees/search-employees";
import { getEasypostShipmentForFulfillmentGroup } from "./schema/fulfillment-groups/get-easypost-shipment-for-fulfillment-group";
import { getFulfillmentGroupById } from "./schema/fulfillment-groups/get-fulfillment-group-by-id";
import { getFulfillmentGroupForShippingClaim } from "./schema/fulfillment-groups/get-fulfillment-group-for-shipping-claim";
import { getFulfillmentGroupRatesInfo } from "./schema/fulfillment-groups/get-fulfillment-group-rates-info";
import { markFulfillmentGroupDeleted } from "./schema/fulfillment-groups/mark-fulfillment-group-deleted";
import { getGmailPromotionTabWhitelistStatus } from "./schema/gmail-promotion-tab-whitelist/get-gmail-promotion-tab-whitelist-status";
import { sendGmailPromotionTabWhitelistRequest } from "./schema/gmail-promotion-tab-whitelist/send-gmail-promotion-tab-whitelist-request";
import { createGoldenDomeNotification } from "./schema/golden-dome-notifications/create-golden-dome-notification";
import { deleteGoldenDomeNotification } from "./schema/golden-dome-notifications/delete-golden-dome-notification";
import { getAllGoldenDomeNotifications } from "./schema/golden-dome-notifications/get-all-golden-dome-notifications";
import { updateGoldenDomeNotification } from "./schema/golden-dome-notifications/update-golden-dome-notification";
import { getGoldenDomeApps } from "./schema/golden-dome/get-golden-dome-apps";
import { getGoldenDomeDataByApp } from "./schema/golden-dome/get-golden-dome-data-by-app";
import { enableIms } from "./schema/ims/enable-ims";
import { getForecastStatus } from "./schema/ims/get-forecast-status";
import { getProductFamilyEmbeddingsStatus } from "./schema/ims/get-product-family-embeddings-status";
import { triggerForecast } from "./schema/ims/trigger-forecast";
import { triggerProductFamilyEmbeddings } from "./schema/ims/trigger-product-family-embeddings";
import { createIncident } from "./schema/incidents/create-incident";
import { deleteIncident } from "./schema/incidents/delete-incident";
import { getIncident } from "./schema/incidents/get-incident";
import { getIncidents } from "./schema/incidents/get-incidents";
import { resolveIncident } from "./schema/incidents/resolve-incident";
import { updateIncident } from "./schema/incidents/update-incident";
import {
  getBoxlessDropoffFees,
  updateBoxlessDropoffFee,
} from "./schema/integrations/boxless-dropoff-fees";
import { getBigcommerceIntegration } from "./schema/integrations/get-bigcommerce-integration";
import { createRedoLandmarkGlobalIntegration } from "./schema/integrations/redo-landmark-global/create-redo-landmark-global-integration";
import { getRedoLandmarkGlobalIntegration } from "./schema/integrations/redo-landmark-global/get-redo-landmark-global-integration";
import { copyProductToAccount } from "./schema/inventory/copy-product-to-account";
import { deleteWarehouseProduct } from "./schema/inventory/delete-warehouse-product";
import {
  getForecastingCostBreakdown,
  saveForecastingCostBreakdown,
} from "./schema/inventory/forecasting-cost-breakdown";
import {
  getEnRouteInventory,
  getEnRouteTeams,
} from "./schema/inventory/get-en-route-inventory";
import { getEnRouteInventoryMetrics } from "./schema/inventory/get-en-route-inventory-metrics";
import { getProductFamilyWithInventory } from "./schema/inventory/get-product-family-with-inventory";
import { getRmrAutomations } from "./schema/inventory/get-rmr-automations";
import { getRmrMerchantInventory } from "./schema/inventory/get-rmr-merchant-inventory";
import { getRmrMerchantMetrics } from "./schema/inventory/get-rmr-merchant-metrics";
import { getRmrMerchants } from "./schema/inventory/get-rmr-merchants";
import { getRmrNetworkSummary } from "./schema/inventory/get-rmr-network-summary";
import { getRmrRefulfillmentMetrics } from "./schema/inventory/get-rmr-refulfillment-metrics";
import { getShippingLabelProducts } from "./schema/inventory/get-shipping-label-products";
import { getWarehouseInventory } from "./schema/inventory/get-warehouse-inventory";
import { getWarehouseInventorySummaryCount } from "./schema/inventory/get-warehouse-inventory-summary-count";
import { incrementInventory } from "./schema/inventory/increment-inventory";
import { manifestReturnedProduct } from "./schema/inventory/manifest-returned-product";
import { scanLabelLookup } from "./schema/inventory/scan-label-lookup";
import { syncSalesData } from "./schema/inventory/sync-sales-data";
import { upsertProductAndInventoryAtLocation } from "./schema/inventory/upsert-product-and-inventory-at-location";
import { upsertProductFamilyAndInventoryAtLocation } from "./schema/inventory/upsert-product-family-and-inventory-at-location";
import { getInternationalOrdersCount } from "./schema/landed-cost/get-international-orders-count";
import { getLandedCostSettings } from "./schema/landed-cost/get-landed-cost-settings";
import { getLcgClassifyProductCounts } from "./schema/landed-cost/get-lcg-classify-product-counts";
import { getLcgClassifyStatus } from "./schema/landed-cost/get-lcg-classify-status";
import { triggerLcgClassify } from "./schema/landed-cost/trigger-lcg-classify";
import { updateLandedCostSettings } from "./schema/landed-cost/update-landed-cost-settings";
import { getTeamLocationsForAdmin } from "./schema/locations/get-team-locations";
import { setLocationLinkedReturnsCenter } from "./schema/locations/set-location-linked-returns-center";
import { toggleLocationReturnsCenter } from "./schema/locations/toggle-location-returns-center";
import { createManualPayoutOrder } from "./schema/manual-payout-order/create-manual-payout-order";
import { deletePayout } from "./schema/manual-payout-order/delete-payout";
import { getOutstandingPayouts } from "./schema/manual-payout-order/get-outstanding-payouts";
import { markPayoutPaid } from "./schema/manual-payout-order/mark-payout-paid";
import { backfillCustomerEvents } from "./schema/marketing/backfill-customer-events";
import { backfillCustomerEventsStatus } from "./schema/marketing/backfill-customer-events-status";
import { createDefaultFlowsAndTemplates } from "./schema/marketing/create-default-flows-and-templates";
import { createExpansionSegments } from "./schema/marketing/create-expansion-segments";
import { enableMarketingAiReplies } from "./schema/marketing/enable-marketing-ai-replies";
import { exportRecoverConversations } from "./schema/marketing/export-recover-conversations";
import { getActualStoreUrl } from "./schema/marketing/get-actual-store-url";
import { getAMPGoogleFormURL } from "./schema/marketing/get-amp-google-form-url";
import { getEmailTemplatesForTeam } from "./schema/marketing/get-email-templates-for-team";
import { getKlaviyoProfilesImportProgress } from "./schema/marketing/get-klaviyo-profiles-import-progress";
import { getSeDashboard } from "./schema/marketing/get-se-dashboard";
import { getTeamsAmpStatus } from "./schema/marketing/get-teams-amp-status";
import { importKlaviyoProfiles } from "./schema/marketing/import-klaviyo-profiles";
import {
  getKlaviyoSubscriptionSync,
  setKlaviyoSubscriptionSync,
} from "./schema/marketing/klaviyo-subscription-sync";
import { sendTestEmail } from "./schema/marketing/send-test-email";
import { startDomainWarming } from "./schema/marketing/start-domain-warming";
import { syncShopifyOrders } from "./schema/marketing/sync-shopify-orders";
import { syncShopifyOrdersStatus } from "./schema/marketing/sync-shopify-orders-status";
import { createPayout } from "./schema/marketplace/create-payout";
import { exportPoshmarkListingsCsv } from "./schema/marketplace/export-poshmark-listings-csv";
import { sendPayouts } from "./schema/marketplace/send-payouts";
import { getMerchantEmailConfigs } from "./schema/merchant-email/get-merchant-email-configs";
import { previewMerchantEmail } from "./schema/merchant-email/preview-merchant-email";
import { sendTestMerchantEmail } from "./schema/merchant-email/send-test-merchant-email";
import { updateMerchantEmailStatus } from "./schema/merchant-email/update-merchant-email-status";
import { upsertMerchantEmailConfig } from "./schema/merchant-email/upsert-merchant-email-config";
import { getMerchantSubmissions } from "./schema/merchant-submissions/get-merchant-submissions";
import { updateMerchantSubmissionStatus } from "./schema/merchant-submissions/update-merchant-submission-status";
import { archiveOnboardingV2Product } from "./schema/onboardings-v2/archive-onboarding-v2-product";
import { exportOnboardingsV2 } from "./schema/onboardings-v2/export-onboardings-v2";
import { getHubspotSyncHealth } from "./schema/onboardings-v2/get-hubspot-sync-health";
import { getOnboardingV2Detail } from "./schema/onboardings-v2/get-onboarding-v2-detail";
import { getOnboardingV2ProductStatuses } from "./schema/onboardings-v2/get-onboarding-v2-product-statuses";
import { getOnboardingWorkflowRuns } from "./schema/onboardings-v2/get-onboarding-workflow-runs";
import { getOnboardingsV2 } from "./schema/onboardings-v2/get-onboardings-v2";
import { getOnboardingsV2Audit } from "./schema/onboardings-v2/get-onboardings-v2-audit";
import { getOnboardingsV2FunnelStats } from "./schema/onboardings-v2/get-onboardings-v2-funnel-stats";
import { getOnboardingsV2FunnelSteps } from "./schema/onboardings-v2/get-onboardings-v2-funnel-steps";
import { getOnboardingsV2OverviewStats } from "./schema/onboardings-v2/get-onboardings-v2-overview-stats";
import { getOnboardingsV2OverviewTimeSeries } from "./schema/onboardings-v2/get-onboardings-v2-overview-time-series";
import { getOnboardingsV2Stats } from "./schema/onboardings-v2/get-onboardings-v2-stats";
import { getOnboardingsV2TimeSeries } from "./schema/onboardings-v2/get-onboardings-v2-time-series";
import { getReturnsActivationFunnel } from "./schema/onboardings-v2/get-returns-activation-funnel";
import { getReturnsChurnBreakdown } from "./schema/onboardings-v2/get-returns-churn-breakdown";
import { getReturnsCohortRetention } from "./schema/onboardings-v2/get-returns-cohort-retention";
import { getReturnsLossReasons } from "./schema/onboardings-v2/get-returns-loss-reasons";
import { getReturnsOwnerPerformance } from "./schema/onboardings-v2/get-returns-owner-performance";
import { getReturnsPipelineFunnel } from "./schema/onboardings-v2/get-returns-pipeline-funnel";
import { getReturnsPreFlowStats } from "./schema/onboardings-v2/get-returns-pre-flow-stats";
import { getReturnsStuckHubspotDeals } from "./schema/onboardings-v2/get-returns-stuck-hubspot-deals";
import { getReturnsStuckOnboardings } from "./schema/onboardings-v2/get-returns-stuck-onboardings";
import { getReturnsVelocityBenchmarks } from "./schema/onboardings-v2/get-returns-velocity-benchmarks";
import { pushV2Configuration } from "./schema/onboardings-v2/push-v2-configuration";
import { triggerHubspotMirrorSync } from "./schema/onboardings-v2/trigger-hubspot-mirror-sync";
import { triggerLifecycleRecompute } from "./schema/onboardings-v2/trigger-lifecycle-recompute";
import { unarchiveOnboardingV2Product } from "./schema/onboardings-v2/unarchive-onboarding-v2-product";
import { updateOnboardingV2Contacted } from "./schema/onboardings-v2/update-onboarding-v2-contacted";
import { updateOnboardingV2DealSize } from "./schema/onboardings-v2/update-onboarding-v2-deal-size";
import { updateV2OnboardingSettings } from "./schema/onboardings-v2/update-v2-onboarding-settings";
import {
  deleteOnboardingConfiguration,
  toggleOnboardingConfigurationActive,
} from "./schema/onboardings/delete-onboarding-configuration";
import { exportFullyOnboardedAudit } from "./schema/onboardings/export-fully-onboarded-audit";
import { exportSelfServeOnboardings } from "./schema/onboardings/export-self-serve-onboardings";
import { exportWeightedAudit } from "./schema/onboardings/export-weighted-audit";
import { getCurrentUserDepartment } from "./schema/onboardings/get-current-user-department";
import { getFirstInstallOnboardingAttribution } from "./schema/onboardings/get-first-install-onboarding-attribution";
import { getFirstInstallOnboardingTeams } from "./schema/onboardings/get-first-install-onboarding-teams";
import { getMerchantInfo } from "./schema/onboardings/get-merchant-info";
import { getMerchantProductStatuses } from "./schema/onboardings/get-merchant-product-statuses";
import { getMerchantSessions } from "./schema/onboardings/get-merchant-sessions";
import { getMerchantShopifyStats } from "./schema/onboardings/get-merchant-shopify-stats";
import { getMyOnboardingStats } from "./schema/onboardings/get-my-onboarding-stats";
import { getMyOnboardings } from "./schema/onboardings/get-my-onboardings";
import { getOnboardingAssignees } from "./schema/onboardings/get-onboarding-assignees";
import {
  getOnboardingConfiguration,
  getOnboardingConfigurationByDomain,
  getOnboardingConfigurations,
} from "./schema/onboardings/get-onboarding-configurations";
import { getOnboardingLeaderboard } from "./schema/onboardings/get-onboarding-leaderboard";
import { getSelfServeOnboardingConversionTrend } from "./schema/onboardings/get-self-serve-onboarding-conversion-trend";
import { getSelfServeOnboardingDetails } from "./schema/onboardings/get-self-serve-onboarding-details";
import { getSelfServeOnboardingFunnelStats } from "./schema/onboardings/get-self-serve-onboarding-funnel-stats";
import { getSelfServeOnboardingFunnelSteps } from "./schema/onboardings/get-self-serve-onboarding-funnel-steps";
import { getSelfServeOnboardingStats } from "./schema/onboardings/get-self-serve-onboarding-stats";
import { getSelfServeOnboardingTimeSeries } from "./schema/onboardings/get-self-serve-onboarding-time-series";
import { getSelfServeOnboardings } from "./schema/onboardings/get-self-serve-onboardings";
import { getSessionDetails } from "./schema/onboardings/get-session-details";
import { markConfigurationPushed } from "./schema/onboardings/mark-configuration-pushed";
import { pushConfigurationToMerchant } from "./schema/onboardings/push-configuration-to-merchant";
import { saveOnboardingConfiguration } from "./schema/onboardings/save-onboarding-configuration";
import { updateSelfServeOnboardingContacted } from "./schema/onboardings/update-self-serve-onboarding-contacted";
import { updateSelfServeOnboardingDealSize } from "./schema/onboardings/update-self-serve-onboarding-deal-size";
import { applyMalomoMigration } from "./schema/order-tracking/apply-malomo-migration";
import { getActiveOrderTrackingIntegrations } from "./schema/order-tracking/get-active-order-tracking-integrations";
import { getMalomoMigrationStatus } from "./schema/order-tracking/get-malomo-migration-status";
import { getOrderTrackingBackfillProgress } from "./schema/order-tracking/get-order-tracking-backfill-progress";
import { klaviyoMetricsHealthCheck } from "./schema/order-tracking/klaviyo-metrics-health-check";
import { scanKlaviyoTemplatesForMalomoLinks } from "./schema/order-tracking/scan-klaviyo-templates-for-malomo-links";
import { sendIntegrationEventTest } from "./schema/order-tracking/send-integration-event-test";
import { triggerOrderTrackingBackfill } from "./schema/order-tracking/trigger-order-tracking-backfill";
import { updateDefaultTrackingPageUrl } from "./schema/order-tracking/update-default-tracking-page-url";
import { addErrorMessage } from "./schema/outbound-labels/add-error-message";
import { deleteErrorMessage } from "./schema/outbound-labels/delete-error-message";
import { getBigcommerceOrderCounts } from "./schema/outbound-labels/get-bigcommerce-order-counts";
import { getErrorMessages } from "./schema/outbound-labels/get-error-messages";
import { getFulfillmentBreakdown } from "./schema/outbound-labels/get-fulfillment-breakdown";
import { getOrderTimeline } from "./schema/outbound-labels/get-order-timeline";
import { getSubscriptionFeeBillingPreview } from "./schema/outbound-labels/get-subscription-fee-billing-preview";
import { getUpsGhostAccounts } from "./schema/outbound-labels/get-ups-ghost-accounts";
import { importPackageLearningCsv } from "./schema/outbound-labels/import-package-learning-csv";
import { importShipStationPackageLearning } from "./schema/outbound-labels/import-shipstation-package-learning";
import {
  getNetsuiteIntegrationSettings,
  syncNetsuiteItemFulfillment,
} from "./schema/outbound-labels/netsuite-integration";
import { onboardWms } from "./schema/outbound-labels/onboard-wms";
import {
  getGroupsNeedingResyncCount,
  getPendingRatesCount,
  refreshTeamRates,
  releaseProblemOrdersWhereNonePicked,
  resyncFulfillmentGroups,
  watchRefreshTeamRatesProgress,
} from "./schema/outbound-labels/refresh-team-rates";
import { resetWmsSot } from "./schema/outbound-labels/reset-wms-sot";
import { updateErrorMessage } from "./schema/outbound-labels/update-error-message";
import { getClaimsCreated } from "./schema/package-protection-plus/get-claims-created";
import { getMerchantClaimRanking } from "./schema/package-protection-plus/get-merchant-claim-ranking";
import { getPackageProtectionPlusCoverageRate } from "./schema/package-protection-plus/get-package-protection-plus-coverage-rate";
import { checkReferralCodeAvailable } from "./schema/partners/check-referral-code-available";
import { createPartner } from "./schema/partners/create-partner";
import { getPartner } from "./schema/partners/get-partner";
import { impersonatePartner } from "./schema/partners/impersonate-partner";
import { listPartnerInstalls } from "./schema/partners/list-partner-installs";
import { listPartners } from "./schema/partners/list-partners";
import { resendMagicLink } from "./schema/partners/resend-magic-link";
import { updatePartner } from "./schema/partners/update-partner";
import { patchStorefrontTreatment } from "./schema/patch-admin-settings/patch-storefront-treatment-schema";
import { patchWidgetConfigs } from "./schema/patch-admin-settings/patch-widget-configs-schema";
import { createPhoneNumber } from "./schema/phone-numbers/create-phone-number";
import { createRecoverProvisioningForTeam } from "./schema/phone-numbers/create-recover-provisioning-for-team";
import { getAllPhoneNumbers } from "./schema/phone-numbers/get-all-phone-numbers";
import { getPhoneNumberByTeamIdAndUseCase } from "./schema/phone-numbers/get-phone-number-by-team-id-and-use-case";
import { getRecoverRegistrationDetail } from "./schema/phone-numbers/get-recover-registration-detail";
import { getTeamRecoverProvisioningDefaults } from "./schema/phone-numbers/get-team-recover-provisioning-defaults";
import { getTollFreeVerificationStatus } from "./schema/phone-numbers/get-toll-free-verification-status";
import { listRecoverRegistrations } from "./schema/phone-numbers/list-recover-registrations";
import { provisionTollFreePhoneNumber } from "./schema/phone-numbers/provision-toll-free-phone-number";
import { retryRecoverProfileAttach } from "./schema/phone-numbers/retry-recover-profile-attach";
import { setRecoverRegistrationDone } from "./schema/phone-numbers/set-recover-registration-done";
import { startProviderTransition } from "./schema/phone-numbers/start-provider-transition";
import { triggerRecoverProvisioning } from "./schema/phone-numbers/trigger-recover-provisioning";
import { updatePhoneNumber } from "./schema/phone-numbers/update-phone-number";
import { getTeamFeatureFlagArm } from "./schema/platform/get-team-feature-flag-arm";
import { getUserFeatureFlag } from "./schema/platform/get-user-feature-flag";
import { setFeatureFlagArm } from "./schema/platform/set-feature-flag-arm";
import { addClusterComment } from "./schema/pm-agent/add-cluster-comment";
import { createLinearTicket } from "./schema/pm-agent/create-linear-ticket";
import { deleteFeatureRequest } from "./schema/pm-agent/delete-feature-request";
import { getAllFeatureRequests } from "./schema/pm-agent/get-all-feature-requests";
import { getClusterDetail } from "./schema/pm-agent/get-cluster-detail";
import { getReviewQueue } from "./schema/pm-agent/get-review-queue";
import { updateClusterStatus } from "./schema/pm-agent/update-cluster-status";
import { generatePointsRedemptionReport } from "./schema/points/generate-points-redemption-report";
import { getPosSettings } from "./schema/pos/get-pos-settings";
import { updatePosSettings } from "./schema/pos/update-pos-settings";
import { getProductFeedbackAnalytics } from "./schema/product-feedback/get-product-feedback-analytics";
import { getProductFeedbackList } from "./schema/product-feedback/get-product-feedback-list";
import { getProductFeedbackScreenshotUrl } from "./schema/product-feedback/get-product-feedback-screenshot-url";
import { updateProductFeedback } from "./schema/product-feedback/update-product-feedback";
import { deleteProductKnowledgeEdit } from "./schema/product-knowledge/delete-product-knowledge-edit";
import { getProductKnowledgeEdit } from "./schema/product-knowledge/get-product-knowledge-edit";
import { getProductKnowledgePageCount } from "./schema/product-knowledge/get-product-knowledge-page-count";
import { listProductKnowledgeEdits } from "./schema/product-knowledge/list-product-knowledge-edits";
import { syncProductKnowledge } from "./schema/product-knowledge/sync-product-knowledge";
import { deleteRateTable } from "./schema/rate-tables/delete-rate-table";
import { getBaselineRateTables } from "./schema/rate-tables/get-baseline-rate-tables";
import { getCarrierDistributionCenterDistances } from "./schema/rate-tables/get-carrier-distribution-center-distances";
import { getDefaultDiscountTables } from "./schema/rate-tables/get-default-discount-table";
import { getDefaultRateTables } from "./schema/rate-tables/get-default-rate-tables";
import { getHistoricalShipmentRates } from "./schema/rate-tables/get-historical-shipment-rates";
import { getRateCalculatorSession } from "./schema/rate-tables/get-rate-calculator-session";
import { getRateCalculatorSessions } from "./schema/rate-tables/get-rate-calculator-sessions";
import { getTeamRateTables } from "./schema/rate-tables/get-rate-tables";
import { getShipmentRateDetails } from "./schema/rate-tables/get-shipment-rate-details";
import { getTeamCrdbLocations } from "./schema/rate-tables/get-team-crdb-locations";
import { getTeamLocations } from "./schema/rate-tables/get-team-locations";
import { getTeamOverrideDiscountTables } from "./schema/rate-tables/get-team-override-discount-tables";
import { getTeamOverrideRateTables } from "./schema/rate-tables/get-team-override-rate-tables";
import { getUspsZoneMappings } from "./schema/rate-tables/get-usps-zone-mappings";
import { parseCarrierInvoiceCsv } from "./schema/rate-tables/parse-carrier-invoice-csv";
import { restoreRateCalculatorSession } from "./schema/rate-tables/restore-rate-calculator-session";
import { saveBaselineRateTables } from "./schema/rate-tables/save-baseline-rate-tables";
import { saveDefaultDiscountTable } from "./schema/rate-tables/save-default-discount-table";
import { saveDefaultRateTable } from "./schema/rate-tables/save-default-rate-table";
import { saveRateCalculatorSession } from "./schema/rate-tables/save-rate-calculator-session";
import { saveTeamRateTable } from "./schema/rate-tables/save-rate-table";
import { saveTeamOverrideDiscountTable } from "./schema/rate-tables/save-team-override-discount-table";
import { saveTeamOverrideRateTable } from "./schema/rate-tables/save-team-override-rate-table";
import { getProductRecommendations } from "./schema/recommendations/get-product-recommendations";
import { createRedoDocumentation } from "./schema/redo-documentation/create-redo-documentation";
import { deleteRedoDocumentation } from "./schema/redo-documentation/delete-redo-documentation";
import { getRedoDocumentationById } from "./schema/redo-documentation/get-redo-documentation-by-id";
import { getRedoDocumentations } from "./schema/redo-documentation/get-redo-documentations";
import { updateRedoDocumentation } from "./schema/redo-documentation/update-redo-documentation";
import { getRateTablesFromDeliveryProfile } from "./schema/redo-in-shipping/get-rate-tables-from-delivery-profile";
import { hasCarrierServices } from "./schema/redo-in-shipping/has-carrier-services";
import { createPriceBook } from "./schema/redo-supplies/create-price-book";
import { createPriceBookEntries } from "./schema/redo-supplies/create-price-book-entries";
import { createPriceBookEntry } from "./schema/redo-supplies/create-price-book-entry";
import { createTrackableProducts } from "./schema/redo-supplies/create-trackable-products";
import { deletePriceBook } from "./schema/redo-supplies/delete-price-book";
import { deletePriceBookEntry } from "./schema/redo-supplies/delete-price-book-entry";
import { deleteSuppliesCollection } from "./schema/redo-supplies/delete-supplies-collection";
import { deleteTrackableProduct } from "./schema/redo-supplies/delete-trackable-product";
import { getPriceBook } from "./schema/redo-supplies/get-price-book";
import { getPriceBookAccounts } from "./schema/redo-supplies/get-price-book-accounts";
import { getPriceBookUniqueValues } from "./schema/redo-supplies/get-price-book-unique-values";
import { getPriceBooks } from "./schema/redo-supplies/get-price-books";
import { getPriceBooksCount } from "./schema/redo-supplies/get-price-books-count";
import { getSuppliesCollectionFilterValues } from "./schema/redo-supplies/get-supplies-collection-filter-values";
import { getSuppliesCollections } from "./schema/redo-supplies/get-supplies-collections";
import { getTrackableProducts } from "./schema/redo-supplies/get-trackable-products";
import { searchProductFamilies } from "./schema/redo-supplies/search-product-families";
import { searchProducts } from "./schema/redo-supplies/search-products";
import { updatePriceBook } from "./schema/redo-supplies/update-price-book";
import { updatePriceBookAccounts } from "./schema/redo-supplies/update-price-book-accounts";
import { updatePriceBookEntry } from "./schema/redo-supplies/update-price-book-entry";
import { updateTrackableProduct } from "./schema/redo-supplies/update-trackable-product";
import { upsertSuppliesCollection } from "./schema/redo-supplies/upsert-supplies-collection";
import { refreshBilling } from "./schema/refresh-billing";
import { getReturnIntegrationMetricsSummary } from "./schema/return-integration-metrics/get-return-integration-metrics-summary";
import { getReturnIntegrationMetricsTable } from "./schema/return-integration-metrics/get-return-integration-metrics-table";
import { getReturnIntegrationMetricsTimeSeries } from "./schema/return-integration-metrics/get-return-integration-metrics-time-series";
import { getReturnIntegrationMonitorSettings } from "./schema/return-integration-metrics/get-return-integration-monitor-settings";
import { saveReturnIntegrationMonitorSettings } from "./schema/return-integration-metrics/save-return-integration-monitor-settings";
import { bulkCreateRmas } from "./schema/returns/bulk-create-rmas";
import { bulkResendLabels } from "./schema/returns/bulk-resend-labels";
import { createOrUpdateLocalizationGroup } from "./schema/returns/create-localization-group";
import { createReturnsCenter } from "./schema/returns/create-returns-center";
import { deleteLocalizationGroup } from "./schema/returns/delete-localization-group";
import { getCommerceCloudBacksyncStatus } from "./schema/returns/get-commerce-cloud-backsync-status";
import { getLocalizationGroups } from "./schema/returns/get-localization-groups";
import { getLocalizationTextOverrides } from "./schema/returns/get-localization-text-overrides";
import { getLoopSyncStatus } from "./schema/returns/get-loop-sync-status";
import { getManagedReturnsIntegrations } from "./schema/returns/get-managed-returns-integrations";
import { getOnboardingOrderCount } from "./schema/returns/get-onboarding-order-count";
import { getReturnForShippingClaim } from "./schema/returns/get-return-for-shipping-claim";
import { getReturnLogicSyncStatus } from "./schema/returns/get-returnlogic-sync-status";
import { getReturnsCenters } from "./schema/returns/get-returns-centers";
import { getTeamDefaultWeight } from "./schema/returns/get-team-default-weight";
import { regenerateCustomCommercialInvoice } from "./schema/returns/regenerate-custom-commercial-invoice";
import { saveLocalizationTextOverrides } from "./schema/returns/save-localization-text-overrides";
import { startCommerceCloudOrderBacksync } from "./schema/returns/start-commerce-cloud-order-backsync";
import { startExternalProviderImport } from "./schema/returns/start-external-return-provider-import";
import { triggerReturnCreationSideEffects } from "./schema/returns/trigger-return-creation-side-effects";
import { updateReturnsCenter } from "./schema/returns/update-returns-center";
import { validateReturnsCenterAddress } from "./schema/returns/validate-returns-center-address";
import { createDefaultReviewForm } from "./schema/reviews/create-default-review-form";
import { searchRurProducts } from "./schema/rur-products/search-rur-products";
import { getUncoveredSalesChannelWarnings } from "./schema/sales-channels/get-uncovered-sales-channel-warnings";
import { assignSendcloudContractToTeam } from "./schema/sendcloud/assign-sendcloud-contract-to-team";
import { listSendcloudPlatformContracts } from "./schema/sendcloud/list-sendcloud-platform-contracts";
import { unassignSendcloudContract } from "./schema/sendcloud/unassign-sendcloud-contract";
import { listServiceInvoices } from "./schema/service-invoices/list-service-invoices";
import { rebillServiceInvoice } from "./schema/service-invoices/rebill-service-invoice";
import {
  approveSettingsChanges,
  rejectSettingsChanges,
} from "./schema/settings-approval/approve-settings-changes";
import { getPendingSettingsChanges } from "./schema/settings-approval/get-pending-settings-changes";
import { previewTeamSettingsChanges } from "./schema/settings-approval/preview-settings-changes";
import { automateShippingClaim } from "./schema/shipping-claims/automate-shipping-claim";
import { generateClaimInvoice } from "./schema/shipping-claims/generate-claim-invoice";
import { generateShippingClaimsCsv } from "./schema/shipping-claims/generate-shipping-claims-csv";
import { getShippingClaimUniqueValues } from "./schema/shipping-claims/get-shipping-claim-unique-values";
import { getShippingClaims } from "./schema/shipping-claims/get-shipping-claims";
import { getShippingClaimsCount } from "./schema/shipping-claims/get-shipping-claims-count";
import { updateShippingClaim } from "./schema/shipping-claims/update-shipping-claim";
import { getShopifyIntegrationSettings } from "./schema/shopify-integration/get-shopify-integration-settings";
import { updateShopifyIntegrationSettings } from "./schema/shopify-integration/update-shopify-integration-settings";
import { getAllDeliveryProfiles } from "./schema/shopify/get-all-delivery-profiles";
import { getCheckoutOutboundRatesSettings } from "./schema/shopify/get-checkout-outbound-rates-settings";
import { getShopifyCatalogs } from "./schema/shopify/get-shopify-catalogs";
import { getShopifyPlan } from "./schema/shopify/get-shopify-plan";
import { refreshShopifyPlan } from "./schema/shopify/refresh-shopify-plan";
import { requestShippingDeliveryPermissions } from "./schema/shopify/request-shipping-delivery-permissions";
import { syncInternationalPriceRounding } from "./schema/shopify/sync-international-price-rounding";
import { syncShopifyProductsForTeam } from "./schema/shopify/sync-shopify-products-for-team";
import { updateCheckoutOutboundRatesSettings } from "./schema/shopify/update-checkout-outbound-rates-settings";
import { concludeShopperAgentSplit } from "./schema/shopper-agent-split/conclude-shopper-agent-split";
import { getShopperAgentSplitAnalytics } from "./schema/shopper-agent-split/get-shopper-agent-split-analytics";
import { getShopperAgentSplits } from "./schema/shopper-agent-split/get-shopper-agent-splits";
import { startShopperAgentSplit } from "./schema/shopper-agent-split/start-shopper-agent-split";
import { createMessagingGroup } from "./schema/slack/create-messaging-group";
import { createTeamSlackChannel } from "./schema/slack/create-team-slack-channel";
import { deleteMessagingGroup } from "./schema/slack/delete-messaging-group";
import { deleteSlackMessage } from "./schema/slack/delete-slack-message";
import { getAskRedoSlackDashboard } from "./schema/slack/get-ask-redo-slack-dashboard";
import { getMessagingGroups } from "./schema/slack/get-messaging-groups";
import { getSlackChannels } from "./schema/slack/get-slack-channels";
import { getSlackMerchantMessages } from "./schema/slack/get-slack-merchant-messages";
import { previewAIResponse } from "./schema/slack/preview-ai-response";
import { sendSlackAIMessage } from "./schema/slack/send-slack-ai-message";
import { sendSlackMessage } from "./schema/slack/send-slack-message";
import { setAskRedoSlackEnabled } from "./schema/slack/set-ask-redo-slack-enabled";
import { setSlackAiBlocked } from "./schema/slack/set-slack-ai-blocked";
import { updateMessagingGroup } from "./schema/slack/update-messaging-group";
import { createSplit } from "./schema/splits/create-split";
import { createTreatment } from "./schema/splits/create-treatment";
import { getSplit } from "./schema/splits/get-split";
import { getTreatments } from "./schema/splits/get-treatments";
import { updateSplit } from "./schema/splits/update-split";
import { updateTreatment } from "./schema/splits/update-treatment";
import { startBilling } from "./schema/start-billing";
import { bulkMigrateTeamsToSupportV2 } from "./schema/support/bulk-migrate-teams-to-support-v2";
import { cancelSupportV2Migration } from "./schema/support/cancel-support-v2-migration";
import { cancelSupportV2Wipe } from "./schema/support/cancel-support-v2-wipe";
import { getConversationInsights } from "./schema/support/conversation-insights/get-conversation-insights";
import { getConversationInsightsAnalytics } from "./schema/support/conversation-insights/get-conversation-insights-analytics";
import { getConversationInsightsCount } from "./schema/support/conversation-insights/get-conversation-insights-count";
import { getSupportAiPricing } from "./schema/support/get-support-ai-pricing";
import { getSupportBackfillMessages } from "./schema/support/get-support-backfill-messages";
import { getSupportConversationExpenses } from "./schema/support/get-support-conversation-expenses";
import { getSupportInvoices } from "./schema/support/get-support-invoices";
import { getSupportV2ConversationCounts } from "./schema/support/get-support-v2-conversation-counts";
import { getSupportV2EntityParity } from "./schema/support/get-support-v2-entity-parity";
import { getSupportV2MigrationAnalytics } from "./schema/support/get-support-v2-migration-analytics";
import { getSupportV2MigrationProgress } from "./schema/support/get-support-v2-migration-progress";
import { getSupportV2ReconcileResult } from "./schema/support/get-support-v2-reconcile-result";
import { getSupportV2TrueupResult } from "./schema/support/get-support-v2-trueup-result";
import { getSupportV2WipeProgress } from "./schema/support/get-support-v2-wipe-progress";
import { importGorgiasFaqs } from "./schema/support/import-gorgias-faqs";
import { importGorgiasKnowledge } from "./schema/support/import-gorgias-knowledge";
import { importGorgiasTags } from "./schema/support/import-gorgias-tags";
import { importGorgiasViews } from "./schema/support/import-gorgias-views";
import { listSupportV2MigrationTeams } from "./schema/support/list-support-v2-migration-teams";
import { migrateTeamToSupportV2 } from "./schema/support/migrate-team-to-support-v2";
import { runBulkSupportV2Finalize } from "./schema/support/run-bulk-support-v2-finalize";
import { runBulkSupportV2Reconcile } from "./schema/support/run-bulk-support-v2-reconcile";
import { runBulkSupportV2Trueup } from "./schema/support/run-bulk-support-v2-trueup";
import { runSupportV2Reconcile } from "./schema/support/run-support-v2-reconcile";
import { runSupportV2Trueup } from "./schema/support/run-support-v2-trueup";
import { toggleInvoiceRefund } from "./schema/support/toggle-invoice-refund";
import { triggerSupportV2EmailBackfill } from "./schema/support/trigger-support-v2-email-backfill";
import { uploadGorgiasMacros } from "./schema/support/upload-gorgias-macros";
import { uploadGorgiasNativeMacros } from "./schema/support/upload-gorgias-native-macros";
import { uploadRichPanelMacros } from "./schema/support/upload-richpanel-macros";
import { wipeAndRemigrateTeamSupportV2 } from "./schema/support/wipe-and-remigrate-team-support-v2";
import { wipeTeamSupportV2 } from "./schema/support/wipe-team-support-v2";
import { getSurchargeDisputesAdmin } from "./schema/surcharge-disputes/get-surcharge-disputes-admin";
import { updateSurchargeDispute } from "./schema/surcharge-disputes/update-surcharge-dispute";
import { _getPickedSettings } from "./schema/team-settings/get-picked-settings";
import { connectTeamTo3pl } from "./schema/team/connect-team-to-3pl";
import { copyCoverageStyling } from "./schema/team/copy-coverage-styling";
import { copyFulfillmentGroupViews } from "./schema/team/copy-fulfillment-group-views";
import { copyLatestFlow } from "./schema/team/copy-latest-flow";
import { copyShopifyIntegration } from "./schema/team/copy-shopify-integration";
import { createTeam } from "./schema/team/create-team";
import { getFulfillForTeams } from "./schema/team/get-fulfills-for-teams";
import { getLatestFlow } from "./schema/team/get-latest-flow";
import { getTeamPrimaryProvider } from "./schema/team/get-team-primary-provider";
import { getTeamSignupAttribution } from "./schema/team/get-team-signup-attribution";
import { getTeamUsers } from "./schema/team/get-team-users";
import { importStripeInfo } from "./schema/team/import-stripe-info";
import { postTeamUserAuth } from "./schema/team/post-team-user-auth";
import { searchTeams } from "./schema/team/search-teams";
import { semanticSearchTeams } from "./schema/team/semantic-search-teams";
import {
  unlinkTeamFromFulfillmentService,
  watchUnlinkTeamFromFulfillmentServiceProgress,
} from "./schema/team/unlink-team-from-fulfillment-service";
import { updateAdminShopperAgentSettings } from "./schema/team/update-admin-shopper-agent-settings";
import { updateTnplSettings } from "./schema/team/update-tnpl-settings";
import { getTemporalActivities } from "./schema/temporal/get-temporal-activities";
import { getWorkflowStatusesBulk } from "./schema/temporal/get-workflow-statuses-bulk";
import { upsertWorkflowStatus } from "./schema/temporal/upsert-workflow-status";
import { addLinearCommentReaction } from "./schema/ticket-validation/add-linear-comment-reaction";
import { autoAssignTicketTeam } from "./schema/ticket-validation/auto-assign-ticket-team";
import { createCampaign } from "./schema/ticket-validation/create-campaign";
import { createLinearIssue } from "./schema/ticket-validation/create-linear-issue";
import { deleteCampaign } from "./schema/ticket-validation/delete-campaign";
import { deleteLinearComment } from "./schema/ticket-validation/delete-linear-comment";
import { deleteTicketValidationResults } from "./schema/ticket-validation/delete-validation-results";
import { editLinearComment } from "./schema/ticket-validation/edit-linear-comment";
import { getCampaignMessages } from "./schema/ticket-validation/get-campaign-messages";
import { getCampaigns } from "./schema/ticket-validation/get-campaigns";
import { getLinearCommentCounts } from "./schema/ticket-validation/get-linear-comment-counts";
import { getLinearComments } from "./schema/ticket-validation/get-linear-comments";
import { getLinearProjects } from "./schema/ticket-validation/get-linear-projects";
import { getLinearTeamLabels } from "./schema/ticket-validation/get-linear-team-labels";
import { getLinearTeams } from "./schema/ticket-validation/get-linear-teams";
import { getLinearUsers } from "./schema/ticket-validation/get-linear-users";
import { getLinearWorkflowStates } from "./schema/ticket-validation/get-linear-workflow-states";
import { getTicketValidationResults } from "./schema/ticket-validation/get-validation-results";
import { postLinearComment } from "./schema/ticket-validation/post-linear-comment";
import { proxyLinearImage } from "./schema/ticket-validation/proxy-linear-image";
import { proxySlackImage } from "./schema/ticket-validation/proxy-slack-image";
import { searchSlackChannels } from "./schema/ticket-validation/search-slack-channels";
import { searchSlackUsers } from "./schema/ticket-validation/search-slack-users";
import { triggerManualValidation } from "./schema/ticket-validation/trigger-manual-validation";
import { updateCampaign } from "./schema/ticket-validation/update-campaign";
import { updateLinearTicketAssignee } from "./schema/ticket-validation/update-linear-ticket-assignee";
import { updateLinearTicketLabels } from "./schema/ticket-validation/update-linear-ticket-labels";
import { updateLinearTicketPriority } from "./schema/ticket-validation/update-linear-ticket-priority";
import { updateLinearTicketSla } from "./schema/ticket-validation/update-linear-ticket-sla";
import { updateLinearTicketStatus } from "./schema/ticket-validation/update-linear-ticket-status";
import { updateLinearTicketTeam } from "./schema/ticket-validation/update-linear-ticket-team";
import { updateTicketFlags } from "./schema/ticket-validation/update-ticket-flags";
import { updateTicketMerchantName } from "./schema/ticket-validation/update-ticket-merchant-name";
import { updateTicketNeedsContext } from "./schema/ticket-validation/update-ticket-needs-context";
import { updateTicketSubmitterName } from "./schema/ticket-validation/update-ticket-submitter-name";
import { updateTicketValidationStatus } from "./schema/ticket-validation/update-ticket-validation-status";
import { uploadLinearFile } from "./schema/ticket-validation/upload-linear-file";
import { getAccountBillingSettings } from "./schema/unified-billing/get-account-billing-settings";
import { getUnifiedBillingEnabled } from "./schema/unified-billing/get-unified-billing-enabled";
import { runUnifiedBillingMigration } from "./schema/unified-billing/run-unified-billing-migration";
import { setUnifiedBillingEnabled } from "./schema/unified-billing/set-unified-billing-enabled";
import { updateBillingSettings } from "./schema/unified-billing/update-billing-settings";
import { uploadFile } from "./schema/upload-file";

const accounts = { getMultipassSecret, setMultipassSecret };

const shippingClaims = {
  getShippingClaims,
  updateShippingClaim,
  getShippingClaimsCount,
  getShippingClaimUniqueValues,
  automateShippingClaim,
  generateClaimInvoice,
  generateShippingClaimsCsv,
};

const fulfillmentGroups = {
  getEasypostShipmentForFulfillmentGroup,
  getFulfillmentGroupById,
  getFulfillmentGroupForShippingClaim,
  getFulfillmentGroupRatesInfo,
  markFulfillmentGroupDeleted,
};

const points = { generatePointsRedemptionReport };

const partners = {
  checkReferralCodeAvailable,
  createPartner,
  getPartner,
  impersonatePartner,
  listPartnerInstalls,
  listPartners,
  resendMagicLink,
  updatePartner,
};

const surchargeDisputes = { getSurchargeDisputesAdmin, updateSurchargeDispute };

const goldenDomeNotifications = {
  createGoldenDomeNotification,
  updateGoldenDomeNotification,
  getAllGoldenDomeNotifications,
  deleteGoldenDomeNotification,
};

const returns = {
  bulkCreateRmas,
  bulkResendLabels,
  regenerateCustomCommercialInvoice,
  createOrUpdateLocalizationGroup,
  createReturnsCenter,
  deleteLocalizationGroup,
  getCommerceCloudBacksyncStatus,
  getLocalizationGroups,
  getLocalizationTextOverrides,
  getOnboardingOrderCount,
  getReturnForShippingClaim,
  getManagedReturnsIntegrations,
  getReturnsCenters,
  getTeamDefaultWeight,
  saveLocalizationTextOverrides,
  updateReturnsCenter,
  validateReturnsCenterAddress,
  getLoopSyncStatus,
  getReturnLogicSyncStatus,
  listSendcloudPlatformContracts,
  assignSendcloudContractToTeam,
  unassignSendcloudContract,
  startCommerceCloudOrderBacksync,
  startExternalProviderImport,
  triggerReturnCreationSideEffects,
};

const conversion = {
  getConversionSettings,
  getIntentStatus,
  getIntentSelfServeTrials,
  getPpuIntentSelfServeMerchants,
  updateConversionSettings,
  updateConversionBillingSettings,
  getCheckoutExperiences,
  canStartHalfRedoCheckoutAbTest,
  canUseShippingOptionMetafields,
  canStartRescuePanelSplit,
  startHalfRedoCheckoutAbTest,
  startRescuePanelSplit,
  concludeRescuePanelSplit,
  getRescuePanelSplitsWithMetrics,
  getTeamCheckoutProbabilitySpotlights,
  executeCheckoutSplitAgent,
  canAutoOnboardCheckoutOptimization,
  getAutoOnboardCheckoutOptimizationStatus,
  triggerAutoOnboardCheckoutOptimization,
  getSyncDeliveryProfileProductMetafieldsNowStatus,
  syncDeliveryProfileProductMetafieldsNow,
  subscriptionsModeAllowed,
  hideInvisibleRateNameCharsAllowed,
  getConversionHealthCheck,
  getLongTermConversionMonitor,
  getConversionTeamDetail,
  getDeliveryProfileProductCount,
  listDeliveryProfileNames,
  bulkUpdateDeliveryProfileProducts,
  splitLocationGroups,
  addRedoCarrierService,
  setupLandedCostDemoCheckoutTree,
  getSetupLandedCostDemoCheckoutTreeStatus,
  getHsaSettings,
  getHsaPaymentAnalytics,
  getHsaPaymentAnalyticsForTeam,
  updateHsaSettings,
  registerGaleMerchant,
  prepareGaleKycLink,
  getGaleOnboardingStatus,
  updateGalePricing,
  reissueGaleApiKey,
  adoptGaleMerchant,
  createCoverageSplitGeneration,
  getCoverageSplitLineage,
};

const customReports = {
  getCustomAnalyticsCatalogPreview,
  getCustomReportDebug,
  getCustomReportsUsage,
  listCustomReports,
  lookupCustomReportId,
};

const locations = {
  getTeamLocationsForAdmin,
  setLocationLinkedReturnsCenter,
  toggleLocationReturnsCenter,
};

const addresses = { getAddressCollectionsForTeam };

const landedCost = {
  getInternationalOrdersCount,
  getLandedCostSettings,
  getLcgClassifyProductCounts,
  getLcgClassifyStatus,
  triggerLcgClassify,
  updateLandedCostSettings,
};

const orderTracking = {
  triggerOrderTrackingBackfill,
  getOrderTrackingBackfillProgress,
  updateDefaultTrackingPageUrl,
  sendIntegrationEventTest,
  getActiveOrderTrackingIntegrations,
  getMalomoMigrationStatus,
  applyMalomoMigration,
  scanKlaviyoTemplatesForMalomoLinks,
  klaviyoMetricsHealthCheck,
};

const ris = {
  getRateTablesFromDeliveryProfile,
  getTeamRateTables,
  saveTeamRateTable,
  deleteRateTable,
  getOrderTimeline,
  getFulfillmentBreakdown,
  getAllDeliveryProfiles,
  hasCarrierServices,
};

const shopifyIntegration = {
  getShopifyIntegrationSettings,
  updateShopifyIntegrationSettings,
};

const defaultRateTables = {
  getDefaultRateTables,
  saveDefaultRateTable,
  getDefaultDiscountTables,
  saveDefaultDiscountTable,
};

const baselineRateTables = {
  getBaselineRateTables,
  saveBaselineRateTables,
  getCarrierDistributionCenterDistances,
  parseCarrierInvoiceCsv,
  getHistoricalShipmentRates,
  getTeamLocations,
  getTeamCrdbLocations,
  getUspsZoneMappings,
  saveRateCalculatorSession,
  getRateCalculatorSession,
  getRateCalculatorSessions,
  restoreRateCalculatorSession,
};

const teamOverrideRateTables = {
  getTeamOverrideRateTables,
  saveTeamOverrideRateTable,
  getTeamOverrideDiscountTables,
  saveTeamOverrideDiscountTable,
};

const outboundLabels = {
  refreshTeamRates,
  watchRefreshTeamRatesProgress,
  getPendingRatesCount,
  resyncFulfillmentGroups,
  getGroupsNeedingResyncCount,
  releaseProblemOrdersWhereNonePicked,
  getNetsuiteIntegrationSettings,
  syncNetsuiteItemFulfillment,
  importPackageLearningCsv,
  importShipStationPackageLearning,
  getSubscriptionFeeBillingPreview,
  getErrorMessages,
  addErrorMessage,
  updateErrorMessage,
  deleteErrorMessage,
  getUpsGhostAccounts,
  onboardWms,
  resetWmsSot,
  getBigcommerceOrderCounts,
};

const pos = { getPosSettings, updatePosSettings };

const integrations = {
  getBigcommerceIntegration,
  getBoxlessDropoffFees,
  updateBoxlessDropoffFee,
  createRedoLandmarkGlobalIntegration,
  getRedoLandmarkGlobalIntegration,
};

const amp = {
  sendDemoAmpEmailsForTeam,
  checkAmpRegistrationStatus,
  getAmpRegistrationFormAnswers,
  getDkimVerifiedFromAddressesForTeam,
  getEmailDomainsForTeam,
  getDefaultSenderAddressForTeam,
};

const gmailPromotionTabWhitelist = {
  getGmailPromotionTabWhitelistStatus,
  sendGmailPromotionTabWhitelistRequest,
};

const redoDocumentation = {
  createRedoDocumentation,
  updateRedoDocumentation,
  deleteRedoDocumentation,
  getRedoDocumentations,
  getRedoDocumentationById,
};

const shopify = {
  getShopifyCatalogs,
  getShopifyPlan,
  refreshShopifyPlan,
  syncInternationalPriceRounding,
};

const team = {
  searchTeams,
  semanticSearchTeams,
  copyFulfillmentGroupViews,
  copyLatestFlow,
  copyShopifyIntegration,
  copyCoverageStyling,
  getLatestFlow,
  _getPickedSettings,
  getTeamPrimaryProvider,
  getTeamSignupAttribution,
  importStripeInfo,
  getTeamUsers,
  postTeamUserAuth,
  updateAdminShopperAgentSettings,
  setCtxPlan,
};

const inventory = {
  getEnRouteInventory,
  getEnRouteInventoryMetrics,
  getEnRouteTeams,
  getProductFamilyWithInventory,
  getRmrAutomations,
  getRmrMerchantInventory,
  getRmrMerchantMetrics,
  getRmrRefulfillmentMetrics,
  getRmrMerchants,
  getRmrNetworkSummary,
  getWarehouseInventory,
  upsertProductAndInventoryAtLocation,
  upsertProductFamilyAndInventoryAtLocation,
  incrementInventory,
  copyProductToAccount,
  manifestReturnedProduct,
  getShippingLabelProducts,
  getWarehouseInventorySummaryCount,
  scanLabelLookup,
  syncSalesData,
  deleteWarehouseProduct,
  getForecastingCostBreakdown,
  saveForecastingCostBreakdown,
};

const marketplace = { createPayout, exportPoshmarkListingsCsv, sendPayouts };

const merchantEmail = {
  getMerchantEmailConfigs,
  previewMerchantEmail,
  sendTestMerchantEmail,
  updateMerchantEmailStatus,
  upsertMerchantEmailConfig,
};

const merchantSubmissions = {
  getMerchantSubmissions,
  updateMerchantSubmissionStatus,
};

const admin = {
  getAuditLog,
  getAuditLogs,
  deleteProductKnowledgeEdit,
  getProductKnowledgeEdit,
  getProductKnowledgePageCount,
  listProductKnowledgeEdits,
  syncProductKnowledge,
  getConversationInsights,
  getConversationInsightsAnalytics,
  getConversationInsightsCount,
  getSupportAiPricing,
  getSupportInvoices,
  getSupportBackfillMessages,
  getSupportConversationExpenses,
  triggerSupportV2EmailBackfill,
  toggleInvoiceRefund,
  getShipmentRateDetails,
  getBalance,
  addBalanceCredit,
  syncShopifyProductsForTeam,
  requestShippingDeliveryPermissions,
  patchWidgetConfigs,
  patchStorefrontTreatment,
  refreshBilling,
  backfillCustomerEventsStatus,
  startBilling,
  getEmailTemplatesForTeam,
  updateTnplSettings,
  getBalanceScheduleInfo,
  upsertBalanceSchedule,
  createTeam,
  connectTeamTo3pl,
  unlinkTeamFromFulfillmentService,
  watchUnlinkTeamFromFulfillmentServiceProgress,
  getFulfillForTeams,
  getAllPhoneNumbers,
  getPhoneNumberByTeamIdAndUseCase,
  createPhoneNumber,
  startProviderTransition,
  getTollFreeVerificationStatus,
  updatePhoneNumber,
  syncShopifyOrdersStatus,
  getCheckoutOutboundRatesSettings,
  updateCheckoutOutboundRatesSettings,
  syncShopifyOrders,
  getCalculatedFulfillmentDelay,
  createSplit,
  updateSplit,
  createTreatment,
  updateTreatment,
  getTreatments,
  getSplit,
  sendTestEmail,
  importKlaviyoProfiles,
  getKlaviyoSubscriptionSync,
  setKlaviyoSubscriptionSync,
  backfillCustomerEvents,
  getKlaviyoProfilesImportProgress,
  provisionTollFreePhoneNumber,
  triggerRecoverProvisioning,
  listRecoverRegistrations,
  setRecoverRegistrationDone,
  retryRecoverProfileAttach,
  getRecoverRegistrationDetail,
  createRecoverProvisioningForTeam,
  getTeamRecoverProvisioningDefaults,
  getAMPGoogleFormURL,
  getSeDashboard,
  createDefaultFlowsAndTemplates,
  createExpansionSegments,
  enableMarketingAiReplies,
  createDefaultReviewForm,
  searchRurProducts,
  createManualPayoutOrder,
  deletePayout,
  markPayoutPaid,
  getOutstandingPayouts,
  createPayouts,
  getTemporalActivities,
  getWorkflowStatusesBulk,
  upsertWorkflowStatus,
  getProductRecommendations,
  startDomainWarming,
  sendSlackMessage,
  previewAIResponse,
  sendSlackAIMessage,
  createTeamSlackChannel,
  deleteSlackMessage,
  getAskRedoSlackDashboard,
  getSlackChannels,
  getSlackMerchantMessages,
  setAskRedoSlackEnabled,
  setSlackAiBlocked,
  getMessagingGroups,
  createMessagingGroup,
  updateMessagingGroup,
  deleteMessagingGroup,
  uploadFile,
  getMostRecentAIContent,
  getAiSettingsRequestStats,
  getGlobalAiExchangeAgentAnalytics,
  getTeamsAmpStatus,
  createCustomerEventsBackfill,
  getCustomerEventsBackfill,
  listCustomerImports,
  startCustomerImport,
  generateReturnInsights,
  getActualStoreUrl,
  exportRecoverConversations,
  getWarehouseSlasDashboard,
};

const aeo = {
  bulkCreateAeoProspects,
  cancelAeoOnboarding,
  checkBrightdataRunStatus,
  exportAeoMarkdown,
  exportAeoPromptResults,
  generateMerchantBrandIdentityGuide,
  getAgentPromptTesterConfig,
  getBatchAeoJobStatus,
  previewAgentPromptTemplate,
  reindexAeoKnowledge,
  getAdminBrandIdentityGuide,
  updateBrandIdentityGuideIdentity,
  getAeoDashboardAnalytics,
  getAeoOnboardingState,
  runAgentPromptTest,
  setAdminBrandAliases,
  startAeoOnboarding,
  startBatchAeoOnboarding,
  startBatchBrandAndCompetitorMention,
  startBatchBrandCompetitorDiscovery,
  startBatchGenerateCompetitorAliases,
  startBatchCompetitorCandidateValidation,
};

const askRedo = {
  exportAskRedoExplorerThreadsCsv,
  getAskRedoChildThreads,
  getAskRedoEmergencyMode,
  getAskRedoExplorerAggregates,
  getAskRedoExplorerThreads,
  getAskRedoThreadById,
  getAskRedoThreads,
  getAskRedoToolMetrics,
  getIntercomDailyMetrics,
  updateAskRedoEmergencyMode,
};

const agents = { generateSettingsAgentResponse };

const platform = {
  setFeatureFlagArm,
  getTeamFeatureFlagArm,
  getUserFeatureFlag,
};

const incidents = {
  createIncident,
  updateIncident,
  getIncident,
  getIncidents,
  deleteIncident,
  resolveIncident,
};

const productFeedback = {
  getProductFeedbackAnalytics,
  getProductFeedbackList,
  getProductFeedbackScreenshotUrl,
  updateProductFeedback,
};

const carrierAccounts = {
  createCarrierAccount,
  createTestLabel,
  deleteCarrierAccount,
  getCarrierAccounts,
};

const brands = { createBrand, getBrands, updateBrand, deleteBrand };

const ims = {
  enableIms,
  getForecastStatus,
  getProductFamilyEmbeddingsStatus,
  triggerForecast,
  triggerProductFamilyEmbeddings,
};

const onboardings = {
  getSelfServeOnboardings,
  exportSelfServeOnboardings,
  exportWeightedAudit,
  exportFullyOnboardedAudit,
  getSelfServeOnboardingDetails,
  getSelfServeOnboardingStats,
  getSelfServeOnboardingTimeSeries,
  getSelfServeOnboardingFunnelStats,
  getSelfServeOnboardingFunnelSteps,
  getSelfServeOnboardingConversionTrend,
  getFirstInstallOnboardingAttribution,
  getFirstInstallOnboardingTeams,
  getMerchantSessions,
  getSessionDetails,
  pushConfigurationToMerchant,
  updateSelfServeOnboardingDealSize,
  updateSelfServeOnboardingContacted,
  markConfigurationPushed,
  getMerchantInfo,
  getMerchantProductStatuses,
  getMerchantShopifyStats,
  // Onboarding configurations
  saveOnboardingConfiguration,
  getOnboardingConfiguration,
  getOnboardingConfigurationByDomain,
  getOnboardingConfigurations,
  getOnboardingAssignees,
  deleteOnboardingConfiguration,
  toggleOnboardingConfigurationActive,
  // My Onboardings (SE Dashboard)
  getMyOnboardingStats,
  getMyOnboardings,
  getOnboardingLeaderboard,
  getCurrentUserDepartment,
  // Onboarding V2
  getOnboardingsV2,
  getOnboardingV2Detail,
  getOnboardingV2ProductStatuses,
  getOnboardingsV2Stats,
  getOnboardingsV2TimeSeries,
  getOnboardingsV2OverviewStats,
  getOnboardingsV2OverviewTimeSeries,
  getOnboardingsV2FunnelStats,
  getOnboardingsV2FunnelSteps,
  getOnboardingsV2Audit,
  getHubspotSyncHealth,
  getOnboardingWorkflowRuns,
  getReturnsActivationFunnel,
  getReturnsChurnBreakdown,
  getReturnsCohortRetention,
  getReturnsLossReasons,
  getReturnsOwnerPerformance,
  getReturnsPipelineFunnel,
  getReturnsPreFlowStats,
  getReturnsStuckHubspotDeals,
  getReturnsStuckOnboardings,
  getReturnsVelocityBenchmarks,
  triggerHubspotMirrorSync,
  triggerLifecycleRecompute,
  exportOnboardingsV2,
  pushV2Configuration,
  archiveOnboardingV2Product,
  unarchiveOnboardingV2Product,
  updateOnboardingV2DealSize,
  updateOnboardingV2Contacted,
  updateV2OnboardingSettings,
};

const auth = { getCurrentUserRole };

const employees = { searchEmployees };

const shopperAi = {
  bulkMigrateTeamsToSupportV2,
  cancelSupportV2Migration,
  cancelSupportV2Wipe,
  importGorgiasFaqs,
  importGorgiasKnowledge,
  importGorgiasTags,
  importGorgiasViews,
  getSupportV2ConversationCounts,
  getSupportV2EntityParity,
  getSupportV2MigrationAnalytics,
  getSupportV2MigrationProgress,
  getSupportV2WipeProgress,
  listSupportV2MigrationTeams,
  migrateTeamToSupportV2,
  runSupportV2Trueup,
  getSupportV2TrueupResult,
  runSupportV2Reconcile,
  getSupportV2ReconcileResult,
  runBulkSupportV2Trueup,
  runBulkSupportV2Finalize,
  runBulkSupportV2Reconcile,
  wipeTeamSupportV2,
  wipeAndRemigrateTeamSupportV2,
  uploadGorgiasMacros,
  uploadGorgiasNativeMacros,
  uploadRichPanelMacros,
};

const shopperAgentSplit = {
  startShopperAgentSplit,
  concludeShopperAgentSplit,
  getShopperAgentSplits,
  getShopperAgentSplitAnalytics,
};

const returnIntegrationMetrics = {
  getReturnIntegrationMetricsSummary,
  getReturnIntegrationMetricsTimeSeries,
  getReturnIntegrationMetricsTable,
  getReturnIntegrationMonitorSettings,
  saveReturnIntegrationMonitorSettings,
};

const ticketValidation = {
  getTicketValidationResults,
  deleteTicketValidationResults,
  updateLinearTicketStatus,
  updateLinearTicketLabels,
  updateLinearTicketPriority,
  updateLinearTicketSla,
  updateLinearTicketTeam,
  updateTicketValidationStatus,
  updateTicketNeedsContext,
  updateTicketFlags,
  updateTicketMerchantName,
  updateTicketSubmitterName,
  getLinearWorkflowStates,
  getLinearTeamLabels,
  getLinearTeams,
  getLinearUsers,
  triggerManualValidation,
  getLinearComments,
  getLinearCommentCounts,
  postLinearComment,
  addLinearCommentReaction,
  editLinearComment,
  deleteLinearComment,
  uploadLinearFile,
  proxyLinearImage,
  proxySlackImage,
  searchSlackUsers,
  searchSlackChannels,
  autoAssignTicketTeam,
  updateLinearTicketAssignee,
  createLinearIssue,
  getLinearProjects,
  getCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getCampaignMessages,
};

const packageProtectionPlus = {
  getClaimsCreated,
  getMerchantClaimRanking,
  getPackageProtectionPlusCoverageRate,
};

const reclaimDisputes = {
  getReclaimMerchants,
  getReclaimMerchantOpportunities,
  getReclaimMerchantOpportunityRows,
  getReclaimMerchantOpportunitySummary,
  getReclaimDisputes,
  getReclaimDisputesCount,
  getReclaimAlerts,
  getReclaimAlertsCount,
  getReclaimDeflections,
  getReclaimDeflectionsCount,
  getReclaimWinRate,
  getReclaimWinRateByReason,
  getReclaimWinRateByBank,
  getReclaimWinRateByReasonAndBank,
  getReclaimWinRateOverTime,
  getReclaimMatureCohorts,
  getReclaimInFlight,
  getReclaimPipelineHealth,
  getReclaimGenerationOverdueDisputes,
  getReclaimDealSizing,
  getReclaimAccessDefaults,
  updateReclaimAccessDefaults,
  setDisputeReviewed,
  regenerateAllDisputeEvidence,
};

const redoSupplies = {
  createPriceBook,
  createPriceBookEntries,
  createPriceBookEntry,
  deletePriceBook,
  deletePriceBookEntry,
  getPriceBook,
  getPriceBookAccounts,
  getPriceBooks,
  getPriceBooksCount,
  getPriceBookUniqueValues,
  createTrackableProducts,
  deleteSuppliesCollection,
  deleteTrackableProduct,
  getSuppliesCollectionFilterValues,
  getSuppliesCollections,
  getTrackableProducts,
  searchProductFamilies,
  searchProducts,
  updatePriceBook,
  updatePriceBookAccounts,
  updatePriceBookEntry,
  updateTrackableProduct,
  upsertSuppliesCollection,
};

const pmAgent = {
  getAllFeatureRequests,
  getReviewQueue,
  getClusterDetail,
  updateClusterStatus,
  addClusterComment,
  createLinearTicket,
  deleteFeatureRequest,
};

const unifiedBilling = {
  getAccountBillingSettings,
  getUnifiedBillingEnabled,
  runUnifiedBillingMigration,
  setUnifiedBillingEnabled,
  updateBillingSettings,
};

const serviceInvoices = { listServiceInvoices, rebillServiceInvoice };

export const rpcDefinition = {
  ...accounts,
  ...shippingClaims,
  ...surchargeDisputes,
  ...fulfillmentGroups,
  ...returns,
  ...conversion,
  ...addresses,
  ...customReports,
  ...landedCost,
  ...locations,
  ...orderTracking,
  ...ris,
  ...shopifyIntegration,
  ...defaultRateTables,
  ...baselineRateTables,
  ...teamOverrideRateTables,
  ...outboundLabels,
  ...amp,
  ...gmailPromotionTabWhitelist,
  ...redoDocumentation,
  ...integrations,
  ...admin,
  ...shopify,
  ...team,
  ...aeo,
  ...askRedo,
  ...agents,
  ...pos,
  ...inventory,
  ...platform,
  ...incidents,
  ...productFeedback,
  ...carrierAccounts,
  ...brands,
  ...ims,
  ...marketplace,
  ...merchantEmail,
  ...merchantSubmissions,
  ...onboardings,
  ...auth,
  ...employees,
  ...shopperAi,
  ...shopperAgentSplit,
  ...returnIntegrationMetrics,
  ...ticketValidation,
  ...pmAgent,
  ...packageProtectionPlus,
  ...reclaimDisputes,
  ...redoSupplies,
  ...points,
  ...partners,
  ...unifiedBilling,
  ...serviceInvoices,
  getExchangeRates,
  getMostRecentAIContent,
  getGoldenDomeDataByApp,
  getGoldenDomeApps,
  ...goldenDomeNotifications,
  getUncoveredSalesChannelWarnings,
  getPendingSettingsChanges,
  previewTeamSettingsChanges,
  approveSettingsChanges,
  rejectSettingsChanges,
};

export type AdminRpcDefinition = InferRpcDefinition<typeof rpcDefinition>;
